import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Users,
  MessageSquare,
  Settings,
  LogOut,
  Home,
  Building2,
  Activity
} from 'lucide-react';
import '../styles/ClinicianDashboard.css'; // Reuse clinician dashboard styles
import userService from '../services/UserService';
import patientService from '../services/PatientService';
import ClinicianList from './components/ClinicianList';
import MessageCenter from './components/MessageCenter';

const SuperAdminDashboard = () => {
  const [activeView, setActiveView] = useState('clinicians');
  const [clinicians, setClinicians] = useState([]);
  const [patientsData, setPatientsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState({ name: 'SuperAdmin', initials: 'SA' });
  const [stats, setStats] = useState({
    totalClinicians: 0,
    totalPatients: 0,
    activePatients: 0,
    unreadMessages: 0
  });

  useEffect(() => {
    loadAdminData();
    loadCliniciansAndPatients();
  }, []);

  const loadAdminData = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setAdmin({
        name: user.name || 'SuperAdmin',
        initials: user.name ? user.name.substring(0, 2).toUpperCase() : 'SA'
      });
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  const loadCliniciansAndPatients = async () => {
    try {
      setLoading(true);

      // Fetch all clinicians
      const cliniciansData = await userService.getUsersByType('clinician');
      console.log('Loaded clinicians:', cliniciansData);

      // Fetch patients for each clinician
      const patientsMap = {};
      let totalActivePatients = 0;

      for (const clinician of cliniciansData) {
        try {
          const patients = await patientService.getPatientsByClinicianId(clinician.id);
          patientsMap[clinician.id] = patients || [];

          // Count active patients
          const activeCount = (patients || []).filter(p => p.status === 'active').length;
          totalActivePatients += activeCount;
        } catch (error) {
          console.error(`Error loading patients for clinician ${clinician.id}:`, error);
          patientsMap[clinician.id] = [];
        }
      }

      setClinicians(cliniciansData);
      setPatientsData(patientsMap);

      // Calculate total patients
      const totalPatients = Object.values(patientsMap).reduce((sum, patients) => sum + patients.length, 0);

      setStats({
        totalClinicians: cliniciansData.length,
        totalPatients: totalPatients,
        activePatients: totalActivePatients,
        unreadMessages: 0 // Will be updated by MessageCenter
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading clinicians and patients:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.reload();
  };

  const navItems = [
    { id: 'clinicians', label: 'Clinicians & Patients', icon: Users },
    { id: 'messages', label: 'Message Center', icon: MessageSquare },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const renderContent = () => {
    switch(activeView) {
      case 'clinicians':
        return (
          <>
            {/* Overview Stats */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Overview</h1>
              <div className="flex gap-6 mb-8">
                <Card className="flex-1">
                  <CardContent className="p-6">
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      Total Clinicians
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {stats.totalClinicians}
                    </p>
                  </CardContent>
                </Card>
                <Card className="flex-1">
                  <CardContent className="p-6">
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      Total Patients
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {stats.totalPatients}
                    </p>
                  </CardContent>
                </Card>
                <Card className="flex-1">
                  <CardContent className="p-6">
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      Active Patients
                    </p>
                    <p className="text-3xl font-bold text-green-600">
                      {stats.activePatients}
                    </p>
                  </CardContent>
                </Card>
                <Card className="flex-1">
                  <CardContent className="p-6">
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      Unread Messages
                    </p>
                    <p className="text-3xl font-bold text-blue-600">
                      {stats.unreadMessages}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Clinician List with nested patients */}
            <ClinicianList
              clinicians={clinicians}
              patientsData={patientsData}
              loading={loading}
              onRefresh={loadCliniciansAndPatients}
            />
          </>
        );

      case 'messages':
        return (
          <MessageCenter
            clinicians={clinicians}
            patientsData={patientsData}
            onUnreadCountChange={(count) => setStats(prev => ({ ...prev, unreadMessages: count }))}
          />
        );

      case 'settings':
        return (
          <Card className="p-12">
            <div className="text-center">
              <Settings className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Settings</h3>
              <p className="text-gray-600">
                Configure your superadmin preferences and system settings
              </p>
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="clinician-dashboard">
      {/* Top Navigation Bar */}
      <header className="top-navbar">
        <div className="navbar-container">
          {/* Logo */}
          <div className="navbar-logo">
            <img src="/auth/logo.svg" alt="MoveMend" className="logo-img" />
          </div>

          {/* Navigation Items */}
          <nav className="navbar-nav">
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`nav-item-horizontal ${activeView === item.id ? 'nav-item-active' : ''}`}
                  onClick={() => setActiveView(item.id)}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Profile and Logout */}
          <div className="navbar-actions">
            <div className="header-profile">
              <Avatar>
                <AvatarFallback className="bg-[#6d4f79] text-white">
                  {admin.initials}
                </AvatarFallback>
              </Avatar>
              <span className="header-name">
                {admin.name.includes('@') ? admin.name.split('@')[0] : admin.name}
              </span>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-content">
        <div className="content-wrapper">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
